/** Client-safe static data. Server-only tables (drops, upgrade rolls) live in ./server. */
import classesJson from "../json/classes.json";
import clockJson from "../json/clock.json";
import combatJson from "../json/combat.json";
import economyJson from "../json/economy.json";
import itemsJson from "../json/items.json";
import mobsJson from "../json/mobs.json";
import moderationJson from "../json/moderation.json";
import movementJson from "../json/movement.json";
import npcsJson from "../json/npcs.json";
import progressionJson from "../json/progression.json";
import pvpJson from "../json/pvp.json";
import questsJson from "../json/quests.json";
import serversJson from "../json/servers.json";
import skillsJson from "../json/skills.json";
import socialJson from "../json/social.json";
import storeJson from "../json/store.json";
import structuresJson from "../json/structures.json";
import upgradeJson from "../json/upgrade.json";
import wheelJson from "../json/wheel.json";
import xpJson from "../json/xp.json";
import zonesJson from "../json/zones.json";
import {
  type ClassDef,
  ClassesSchema,
  type ClassId,
  ClockSchema,
  CombatSchema,
  EconomySchema,
  type ItemDef,
  ItemsSchema,
  type MobDef,
  MobsSchema,
  ModerationSchema,
  MovementSchema,
  type NpcDef,
  NpcsSchema,
  ProgressionSchema,
  PvpSchema,
  type QuestDef,
  QuestsSchema,
  type ServerDef,
  ServersSchema,
  type SkillDef,
  SkillsSchema,
  SocialSchema,
  StoreSchema,
  type StructureDef,
  StructuresSchema,
  UpgradeTablesSchema,
  WheelSchema,
  XpTableSchema,
  type Zone,
  ZonesSchema,
} from "./schema";
import { validateReferences } from "./validate";

export {
  type DerivedStats,
  deriveStats,
  type EquippedItem,
  type GearBonus,
  gearBonus,
  JEWELRY_SLOTS,
  levelShare,
  mobLevelMult,
  NO_UPGRADE_SLOTS,
  plusMultiplier,
  type StatBlock,
  upgradeTableFor,
} from "./derive";

import { deriveStats, upgradeTableFor as ladderFor, levelShare } from "./derive";

export { sellPrice } from "./pricing";
export * from "./schema";
export { validateReferences } from "./validate";
export { buildXpTable, mobXpMult, xpForLevel } from "./xp";

export const progression = ProgressionSchema.parse(progressionJson);
export const movement = MovementSchema.parse(movementJson);
export const xpTable = XpTableSchema.parse(xpJson);
/**
 * The Crystal Clash arena is one map in zones.json played in one room per level bracket: it
 * loads as a copy per bracket, `<id>_1`, `<id>_2`... with `clash.bracket` set.
 */
function expandArenas(list: Zone[]): Zone[] {
  return list.flatMap((z) =>
    z.clash
      ? z.clash.brackets.map((_, i) => ({
          ...z,
          id: `${z.id}_${i + 1}`,
          clash: { ...(z.clash as NonNullable<Zone["clash"]>), bracket: i },
        }))
      : [z],
  );
}
export const zones = expandArenas(ZonesSchema.parse(zonesJson));
export const combat = CombatSchema.parse(combatJson);

/**
 * A class's usual max MP at a level: its base stats with every stat point in its primary stat,
 * no gear. Skill costs and the blue potion follow it, so they grow as the pools do.
 */
export function typicalMaxMp(cls: ClassDef, level: number, cfg = combat): number {
  const stats = { ...cls.baseStats };
  stats[cls.primary] += progression.statPointsPerLevel * Math.max(0, level - 1);
  return deriveStats(cls, stats, cfg, {}, level).maxMp;
}

/** What a skill costs a caster of this class and level (classes.json `mpCostRef`). */
export function skillMpCost(skill: SkillDef, cls: ClassDef, level: number, cfg = combat): number {
  if (skill.mpCost <= 0) return 0;
  return Math.max(1, Math.round((skill.mpCost * typicalMaxMp(cls, level, cfg)) / cls.mpCostRef));
}

/** What a character's endless potion restores at a level (HP, or MP for the class). */
export function potionAmount(
  kind: "hp" | "mp",
  level: number,
  cls: ClassDef,
  cfg = combat,
): number {
  if (kind === "mp")
    return Math.round((typicalMaxMp(cls, level, cfg) * cfg.potions.mpPoolPct) / 100);
  // Pools grow with level (levelGrowth), so the potion does too.
  const grow = 1 + levelShare(level, cfg) * cfg.levelGrowth.potionBonus;
  return Math.round((cfg.potions.hpBase + cfg.potions.hpPerLevel * level) * grow);
}

/** How long a potion of this kind takes to be ready again. */
export const potionCooldownMs = (kind: "hp" | "mp", cfg = combat): number =>
  kind === "mp" ? cfg.potions.mpCooldownMs : cfg.potions.cooldownMs;
export const clock = ClockSchema.parse(clockJson);

/** Game hour (0..24) at a real time, shared by everyone. */
export function gameHour(nowMs: number): number {
  return ((((nowMs / clock.dayLengthMs) * 24 + clock.hourOffset) % 24) + 24) % 24;
}
export const classes = ClassesSchema.parse(classesJson);
export const skills = SkillsSchema.parse(skillsJson);
export const mobs = MobsSchema.parse(mobsJson);
export const structures = StructuresSchema.parse(structuresJson);
export const items = ItemsSchema.parse(itemsJson);
export const economy = EconomySchema.parse(economyJson);
export const npcs = NpcsSchema.parse(npcsJson);
export const pvp = PvpSchema.parse(pvpJson);
export const social = SocialSchema.parse(socialJson);
export const questData = QuestsSchema.parse(questsJson);
/** The Riftstone Wheel's segments (the odds are server-only). */
export const wheel = WheelSchema.parse(wheelJson);
export const quests = questData.quests;
export const servers: ServerDef[] = ServersSchema.parse(serversJson);
export const getServer = (id: string): ServerDef | undefined => servers.find((s) => s.id === id);
export const store = StoreSchema.parse(storeJson);
/** Item templates sold for gems (Wisps, Fortune Stones): they can never be thrown away. */
const STORE_ITEMS = new Set([
  ...store.glows.map((g) => g.item),
  ...store.wings.map((w) => w.item),
  ...store.cloaks.map((c) => c.item),
  ...store.items.map((i) => i.item),
]);
export const isStoreItem = (templateId: string): boolean => STORE_ITEMS.has(templateId);
/**
 * Materials worth keeping: what a shop sells (Gold Bars, gold kept in the bag) and the Riftstones
 * the Reel spins. A merchant's "+ Materials" button never adds them to a sale.
 */
const KEPT_MATERIALS = new Set([
  ...npcs.flatMap((n) => (n.shop ?? []).map((o) => o.item)),
  ...Object.values(wheel.tiers).map((tier) => tier.gem),
]);
/** A monster's loot material, sold in bulk with the "+ Materials" button. */
export const isLootMaterial = (templateId: string): boolean =>
  getItem(templateId).kind === "material" && !KEPT_MATERIALS.has(templateId);
export const moderation = ModerationSchema.parse(moderationJson);

/** Clan names: letters, digits and single spaces, no leading or trailing space. */
export function validClanName(name: string): boolean {
  const { nameMin, nameMax } = social.clan;
  return (
    name.length >= nameMin && name.length <= nameMax && /^[A-Za-z0-9]+( [A-Za-z0-9]+)*$/.test(name)
  );
}

/** Rank symbol index for lifetime NP earned: 0 = none, 1 = pvp.ranks[0], ... */
/**
 * The one place symbol a player shows (owner decision 2026-10-01): the ranking of all time
 * comes first, its place boxed; without one, this month's place plain. Null without a symbol
 * place.
 */
export function shownPlace(month: number, all: number): { place: number; boxed: boolean } | null {
  if (placeSymbol(all)) return { place: all, boxed: true };
  if (placeSymbol(month)) return { place: month, boxed: false };
  return null;
}

/**
 * What a GM of this class shows (moderation.json gmLook): slot and item template, in the
 * order the game sends gear. A one-handed set weapon is taken first so a shield fits beside.
 */
export function gmGear(cls: ClassId): { slot: string; templateId: string }[] {
  const { tier, cape } = moderation.gmLook;
  const own = items.filter((i) => i.tier === tier && i.classes?.includes(cls));
  const pick = (slot: string) => {
    const of = own.filter((i) => i.slot === slot);
    return of.find((i) => !i.twoHanded) ?? of[0];
  };
  const out: { slot: string; templateId: string }[] = [];
  const weapon = pick("weapon");
  for (const slot of ["weapon", "offhand", "head", "chest", "hands", "feet"]) {
    const it = pick(slot);
    if (!it || (slot === "offhand" && weapon?.twoHanded)) continue;
    out.push({ slot, templateId: it.id });
  }
  out.push({ slot: "cape", templateId: cape });
  return out;
}

/** The place symbol (1-based index into pvp.placeSymbols) for a ranking place, or 0. */
export function placeSymbol(place: number): number {
  if (place < 1) return 0;
  return pvp.placeSymbols.findIndex((s) => place <= s.upTo) + 1;
}

export function npRank(npEarned: number): number {
  let rank = 0;
  pvp.ranks.forEach((r, i) => {
    if (npEarned >= r.minNp) rank = i + 1;
  });
  return rank;
}
/** PRD: upgrade odds are public (the blacksmith shows the exact chance); rolls stay on the server. */
export const upgradeTables = UpgradeTablesSchema.parse(upgradeJson);

validateReferences({
  zones,
  classes,
  skills,
  mobs,
  structures,
  items,
  npcs,
  quests,
  wheel,
  economy,
});

const byId = <T extends { id: string }>(list: T[], kind: string) => {
  const map = new Map(list.map((x) => [x.id, x]));
  return (id: string): T => {
    const found = map.get(id);
    if (!found) throw new Error(`Unknown ${kind}: ${id}`);
    return found;
  };
};

export const getZone: (id: string) => Zone = byId(zones, "zone");
export const getQuest: (id: string) => QuestDef = byId(quests, "quest");

/**
 * Neutral ground: no PvP and no nation's homeland (Hearthmoor). Both nations may party and
 * trade there (owner decision 2026-10-01); elsewhere only their own nation.
 */
export function isNeutralZone(zone: Zone): boolean {
  return !zone.pvp && !zone.nation;
}

/** Where a character of this race arrives and respawns in a zone. */
export function spawnFor(zone: Zone, race: string): { x: number; z: number } {
  const bases = zone.bases;
  if (bases && (race === "human" || race === "orc")) return bases[race];
  return zone.spawn;
}
export const getClass: (id: ClassId) => ClassDef = byId(classes, "class");
export const getSkill: (id: string) => SkillDef = byId(skills, "skill");
export const getMob: (id: string) => MobDef = byId(mobs, "mob");
export const getStructure: (id: string) => StructureDef = byId(structures, "structure");
export const getItem: (id: string) => ItemDef = byId(items, "item");
export const getNpc: (id: string) => NpcDef = byId(npcs, "npc");

/** A zone a travel gate can take you to. */
export interface Destination {
  zone: Zone;
  /** Level needed to travel there. */
  minLevel: number;
  /** Lowest and highest level of its regular mobs (bosses left out), null if none. */
  mobLevels: [number, number] | null;
}

/**
 * Where a gate in `fromId` can take a character of this race: every zone except the one you
 * are in and the other nation's homeland, lowest level first. Locked ones are listed too so
 * players see what is ahead; the server refuses them.
 */
export function travelDestinations(fromId: string, race: string): Destination[] {
  return zones
    .filter((z) => z.id !== fromId && !z.timed && !z.clash && (!z.nation || z.nation === race))
    .map((zone) => {
      const levels = zone.mobSpawns.map((s) => getMob(s.mob)).filter((m) => !m.boss);
      const lv = levels.map((m) => m.level);
      return {
        zone,
        minLevel: zone.minLevel ?? 1,
        mobLevels: lv.length > 0 ? ([Math.min(...lv), Math.max(...lv)] as [number, number]) : null,
      };
    })
    .sort((a, b) => a.minLevel - b.minLevel);
}

/** Where one gate leads: a gate with `only` goes to that zone alone, others anywhere. */
export function gateDestinations(fromId: string, index: number, race: string): Destination[] {
  const gate = getZone(fromId).portals?.[index];
  if (!gate) return [];
  if (!gate.only) return travelDestinations(fromId, race);
  const zone = getZone(gate.only);
  return [{ zone, minLevel: zone.minLevel ?? 1, mobLevels: mobLevelRange(zone) }];
}

function mobLevelRange(zone: Zone): [number, number] | null {
  const lv = zone.mobSpawns
    .map((s) => getMob(s.mob))
    .filter((m) => !m.boss)
    .map((m) => m.level);
  return lv.length > 0 ? [Math.min(...lv), Math.max(...lv)] : null;
}

/** A clan's grade (1 is the best) for its members' lifetime National Points added up. */
export function clanGrade(totalNp: number): number {
  let grade = social.clan.grades[0]?.grade ?? 5;
  for (const g of social.clan.grades) if (totalNp >= g.minNp) grade = g.grade;
  return grade;
}

/** National Points a clan needs for its next grade, or null at the top. */
export function clanNextGradeNp(totalNp: number): number | null {
  const next = social.clan.grades.find((g) => g.minNp > totalNp);
  return next ? next.minNp : null;
}

/** The Crystal Clash settings (shared by every bracket's arena), if the data has an arena. */
export const clash = zones.find((z) => z.clash)?.clash ?? null;

/** The arena of each Crystal Clash bracket, in bracket order. */
export const clashArenas: Zone[] = zones.filter((z) => z.clash);

/** The bracket a character of this level plays the Crystal Clash in, or -1 when none. */
export function clashBracketFor(level: number): number {
  return clash ? clash.brackets.findIndex((b) => level >= b.minLevel && level <= b.maxLevel) : -1;
}

export function zoneNpcs(zoneId: string): NpcDef[] {
  return npcs.filter((n) => n.zone === zoneId);
}

/** Equipment slots in display order; `ring` appears twice. */
export const EQUIP_SLOTS = [
  "weapon",
  "offhand",
  "head",
  "chest",
  "hands",
  "feet",
  "ring1",
  "ring2",
  "neck",
  "earring1",
  "earring2",
  "belt",
  "cape",
  "glow",
  "wings",
  "reward",
] as const;
export type EquipSlot = (typeof EQUIP_SLOTS)[number];
export const BAG_SIZE = 56;

/** Which equipment slots an item may go into. */
/** Items with a + ladder (weapons, armor, jewelry, cloaks): their + always shows, even +0. */
export function upgradable(templateId: string): boolean {
  return !!ladderFor(upgradeTables, getItem(templateId).slot);
}

/** The + an item comes with wherever it is made: weapons and armor +1, the rest +0. */
export function defaultPlus(templateId: string): number {
  const kind = getItem(templateId).kind;
  return kind === "weapon" || kind === "armor" ? 1 : 0;
}

/** An item's name as players see it everywhere: "Iron Sword +1", "Copper Ring +0". */
export function itemLabel(item: { templateId: string; plus: number }): string {
  const name = getItem(item.templateId).name;
  return upgradable(item.templateId) ? `${name} +${item.plus}` : name;
}

/** The + badge on an item slot: "+2", "+0" on anything with a ladder, "" otherwise. */
export function plusBadge(item: { templateId: string; plus: number }): string {
  return upgradable(item.templateId) ? `+${item.plus}` : "";
}

export function slotsFor(item: ItemDef): EquipSlot[] {
  if (item.slot === "none") return [];
  if (item.slot === "ring") return ["ring1", "ring2"];
  if (item.slot === "earring") return ["earring1", "earring2"];
  // A store cloak takes the Cloak slot: one cloak at a time (owner decision 2026-10-02).
  if (item.slot === "mantle") return ["cape"];
  return [item.slot];
}

/** Skills of a class ordered by unlock level (skill bar order). */
export function classSkills(classId: ClassId): SkillDef[] {
  return skills.filter((s) => s.classId === classId).sort((a, b) => a.unlockLevel - b.unlockLevel);
}

/** XP needed to go from `level` to `level + 1` (0 at the cap). */
export function xpToNext(level: number): number {
  return xpTable.find((r) => r.level === level)?.xp ?? 0;
}
